import { ActiveSubscriptionStatuses, WorkspaceInvitationState, WorkspaceUserProfile } from 'models/user'
import { useCallback, useMemo } from 'react'

import { useRememberWorkspace } from './useRememberWorkspace'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Custom hook to manage workspace list logic for TopBar dropdowns.
 * Consolidates shared logic between WorkspaceDropdown and WorkspaceDropdownMobile.
 */
export const useWorkspaceList = () => {
  const { visiblePersonalUserWorkspaces, pendingInvitationWorkspaceList, activeSubscribedUserWorkspaces, setCurrentUserWorkspace, currentUserWorkspace, hasUserActiveSubscription, isWorkspaceResolved, userWorkspacesQuery } = useUserData()

  const rememberWorkspace = useRememberWorkspace()

  /**
   * The workspace to display in the TopBar.
   * If there's a pending invitation and NO active subscription, show the first pending workspace.
   * Otherwise, show the current user workspace.
   */
  const displayedWorkspace = useMemo(() => {
    if (pendingInvitationWorkspaceList.length && !hasUserActiveSubscription) {
      return pendingInvitationWorkspaceList[0]
    }

    return currentUserWorkspace
  }, [pendingInvitationWorkspaceList, hasUserActiveSubscription, currentUserWorkspace])

  /** Combined list of pending invitation workspaces, active subscribed workspaces, and personal workspaces */
  const workspaceList = useMemo(() => {
    const allWorkspaces = Array.from(new Map([...pendingInvitationWorkspaceList, ...activeSubscribedUserWorkspaces, ...visiblePersonalUserWorkspaces].map((w) => [w.id, w])).values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    // Move the displayed workspace to the first position
    if (displayedWorkspace) {
      const sortedList = allWorkspaces.filter((w) => w.id !== displayedWorkspace.id)
      return [displayedWorkspace, ...sortedList]
    }

    return allWorkspaces
  }, [activeSubscribedUserWorkspaces, pendingInvitationWorkspaceList, visiblePersonalUserWorkspaces, displayedWorkspace])

  /** Check if a workspace has a pending invitation */
  const isWorkspacePending = useCallback((workspace: WorkspaceUserProfile) => {
    return ActiveSubscriptionStatuses.has(workspace.status) && workspace.hasOwnProperty('invitationId') && workspace.invitationState === WorkspaceInvitationState.PENDING
  }, [])

  /**
   * Switches the active workspace and remembers the choice, so logging in on another browser or device
   * lands on it. Both TopBar switchers go through here — the desktop and mobile dropdowns must not diverge
   * on which selections are remembered.
   *
   * A pending invitation is switched into but not remembered: there is no membership row until it is
   * accepted, so the backend would reject it as forbidden. The accept flow records it instead, once the
   * membership exists.
   */
  const selectWorkspace = useCallback((workspace: WorkspaceUserProfile) => {
    setCurrentUserWorkspace(workspace)

    // Re-selecting the workspace already displayed is a no-op worth skipping: the switcher keeps the
    // current one as its first row, so it is the easiest row to click by accident.
    if (workspace.id === currentUserWorkspace?.id) return
    if (isWorkspacePending(workspace)) return
    rememberWorkspace(workspace.id)
  }, [setCurrentUserWorkspace, currentUserWorkspace?.id, isWorkspacePending, rememberWorkspace])

  /** Workspace list filtered to only active subscription statuses (for dropdown selectors) */
  const activeWorkspaceList = useMemo(() => workspaceList.filter((w) => ActiveSubscriptionStatuses.has(w.status)), [workspaceList])

  /** Whether the user has at least one pending workspace invitation across any of their workspaces */
  const hasPendingInvitations = useMemo(() => pendingInvitationWorkspaceList.length > 0, [pendingInvitationWorkspaceList])

  /**
   * Looks up the enriched `/user/workspace` membership (ccEmails, Propstack fields, etc.) for a
   * switcher row. `activeWorkspaceList`/`workspaceList` items are `WorkspaceUserProfile` (from
   * `userMe`), a different, id-keyed shape that doesn't carry `propstackLinked`/
   * `propstackBannerDismissed` — those only exist on `UserWorkspaceMembership` (ISSUE-2136).
   */
  const getPropstackMembership = useCallback(
    (workspaceId: string) => userWorkspacesQuery.data?.workspaces?.find((ws) => ws.workspaceId === workspaceId),
    [userWorkspacesQuery.data]
  )

  return {
    displayedWorkspace,
    workspaceList,
    activeWorkspaceList,
    isWorkspacePending,
    isWorkspaceResolved,
    hasPendingInvitations,
    selectWorkspace,
    getPropstackMembership,
  }
}
